import Card from '../../components/ui/Card'
import { formatCurrency } from '../../lib/format'

function ProfitLossStatement({ transactions, dateRange, businessName }) {
  // Group revenue by category
  const revenueByCategory = transactions
    .filter(t => t.type === 'revenue' || t.type === 'income')
    .reduce((acc, t) => {
      const category = t.category || 'Other Revenue'
      if (!acc[category]) acc[category] = 0
      acc[category] += parseFloat(t.amount)
      return acc
    }, {})

  // Group expenses by category
  const expensesByCategory = transactions
    .filter(t => t.type === 'expense')
    .reduce((acc, t) => {
      const category = t.category || 'Other Expenses'
      if (!acc[category]) acc[category] = 0
      acc[category] += parseFloat(t.amount)
      return acc
    }, {})

  const totalRevenue = Object.values(revenueByCategory).reduce((sum, v) => sum + v, 0)
  const totalExpenses = Object.values(expensesByCategory).reduce((sum, v) => sum + v, 0)
  const netProfit = totalRevenue - totalExpenses
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0

  const revenueCategories = Object.entries(revenueByCategory).sort((a, b) => b[1] - a[1])
  const expenseCategories = Object.entries(expensesByCategory).sort((a, b) => b[1] - a[1])

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="card-title mb-1">Profit & Loss Statement</h3>
          <p className="text-sm text-muted">
            {businessName || 'All Businesses'} | {dateRange || 'All Time'}
          </p>
        </div>
      </div>

      <div className="pnl-statement">
        {/* Revenue Section */}
        <div className="pnl-section mb-4">
          <div className="pnl-header" style={{
            background: 'var(--success-light)',
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '8px',
          }}>
            <span className="font-medium" style={{ color: 'var(--success)' }}>Revenue</span>
          </div>

          {revenueCategories.length > 0 ? (
            <>
              {revenueCategories.map(([category, amount]) => (
                <div key={category} className="flex justify-between py-2 px-3 border-b" style={{ borderColor: 'var(--gray-100)' }}>
                  <span className="text-sm">{category}</span>
                  <span className="text-sm">{formatCurrency(amount)}</span>
                </div>
              ))}
              <div className="flex justify-between py-2 px-3 font-medium" style={{ background: 'var(--gray-50)' }}>
                <span>Total Revenue</span>
                <span style={{ color: 'var(--success)' }}>{formatCurrency(totalRevenue)}</span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted py-2 px-3">No revenue recorded</p>
          )}
        </div>

        {/* Expenses Section */}
        <div className="pnl-section mb-4">
          <div className="pnl-header" style={{
            background: 'var(--error-light)',
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '8px',
          }}>
            <span className="font-medium" style={{ color: 'var(--error)' }}>Expenses</span>
          </div>

          {expenseCategories.length > 0 ? (
            <>
              {expenseCategories.map(([category, amount]) => (
                <div key={category} className="flex justify-between py-2 px-3 border-b" style={{ borderColor: 'var(--gray-100)' }}>
                  <span className="text-sm">{category}</span>
                  <span className="text-sm">{formatCurrency(amount)}</span>
                </div>
              ))}
              <div className="flex justify-between py-2 px-3 font-medium" style={{ background: 'var(--gray-50)' }}>
                <span>Total Expenses</span>
                <span style={{ color: 'var(--error)' }}>{formatCurrency(totalExpenses)}</span>
              </div>
            </>
          ) : (
            <p className="text-sm text-muted py-2 px-3">No expenses recorded</p>
          )}
        </div>

        {/* Net Profit Section */}
        <div className="pnl-summary" style={{
          background: netProfit >= 0 ? 'var(--success-light)' : 'var(--error-light)',
          padding: '16px',
          borderRadius: '8px',
        }}>
          <div className="flex justify-between items-center">
            <div>
              <span className="font-bold text-lg">Net Profit</span>
              {totalRevenue > 0 && (
                <span className="text-sm text-muted ml-2">
                  ({profitMargin.toFixed(1)}% margin)
                </span>
              )}
            </div>
            <span
              className="text-2xl font-bold"
              style={{ color: netProfit >= 0 ? 'var(--success)' : 'var(--error)' }}
            >
              {formatCurrency(netProfit)}
            </span>
          </div>

          <div className="flex gap-8 mt-4 text-sm">
            <div>
              <span className="text-muted">Revenue:</span>
              <span className="ml-2 font-medium">{formatCurrency(totalRevenue)}</span>
            </div>
            <div>
              <span className="text-muted">Expenses:</span>
              <span className="ml-2 font-medium">{formatCurrency(totalExpenses)}</span>
            </div>
            <div>
              <span className="text-muted">Transactions:</span>
              <span className="ml-2 font-medium">{transactions.length}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

export default ProfitLossStatement
